import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type React from "react";
import { useState } from "react";
import { Toaster } from "react-hot-toast";
import {
	Navigate,
	Route,
	BrowserRouter as Router,
	Routes,
	useParams,
} from "react-router-dom";
import AIAssistant from "./components/AIAssistant";
import Header from "./components/Header";
import ProtectedRoute from "./components/ProtectedRoute";
import { RouteInvalidationHandler } from "./components/RouteInvalidationHandler";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DocumentTemplateProvider } from "./context/DocumentTemplateContext";
import { ThemeProvider } from "./context/ThemeContext";
import { WalletProvider } from "./context/WalletContext";
import { ConfirmDialogProvider } from "./hooks/useConfirmDialog";
import AddWallet from "./pages/AddWallet";
import ApplicationDetail from "./pages/ApplicationDetail";
import Applications from "./pages/Applications";
import Dashboard from "./pages/Dashboard";
import EditWallet from "./pages/EditWallet";
import KYCForm from "./pages/KYCForm";
import KYCList from "./pages/KYCList";
import Login from "./pages/Login";
import Onboard from "./pages/Onboard";
import PartnerDashboard from "./pages/PartnerDashboard";
import Settings from "./pages/Settings";
import SettlementAnalysis from "./pages/SettlementAnalysis";
import StellarAdmin from "./pages/StellarAdmin";
import Unauthorized from "./pages/Unauthorized";
import UserManagement from "./pages/UserManagement";
import WalletDetail from "./pages/WalletDetail";
import { createTrpcClient, trpc } from "./trpc";
import "./styles/global.css";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 60_000, // 1 min - reduce refetches to avoid 429
			retry: (failureCount, error) => {
				// Don't retry on 429 - back off
				if (
					error &&
					typeof error === "object" &&
					"status" in error &&
					error.status === 429
				) {
					return false;
				}
				return failureCount < 1;
			},
		},
	},
});

// Wrapper to determine if user should see partner view
const WalletDetailWrapper: React.FC = () => {
	const { hasAdminAccess } = useAuth();
	// Admin and stellar_admin see admin view; partner sees partner view
	return <WalletDetail isPartnerView={!hasAdminAccess} />;
};

// Wrapper for EditWallet to check wallet access
const EditWalletWrapper: React.FC = () => {
	const { isAdmin, isStellarAdmin, user } = useAuth();
	const { id: walletId } = useParams<{ id: string }>();

	// Stellar admin cannot edit wallets
	if (isStellarAdmin) {
		return <Navigate to="/unauthorized" replace />;
	}

	// Admin can edit any wallet, partners can only edit their assigned wallets
	if (!isAdmin && user && !(user.wallet_ids ?? []).includes(walletId ?? "")) {
		return <Navigate to="/unauthorized" replace />;
	}

	return <EditWallet />;
};

const AppRoutes: React.FC = () => {
	const { isAuthenticated, hasAdminAccess } = useAuth();

	return (
		<Routes>
			<Route
				path="/login"
				element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
			/>

			<Route path="/unauthorized" element={<Unauthorized />} />

			{/* Admin Only: Onboarding Applications */}
			<Route
				path="/applications"
				element={
					<ProtectedRoute requireFullAdmin>
						<Applications />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/applications/:id"
				element={
					<ProtectedRoute requireFullAdmin>
						<ApplicationDetail />
					</ProtectedRoute>
				}
			/>

			{/* Root Route - Redirects based on role */}
			<Route
				path="/"
				element={
					<ProtectedRoute>
						{hasAdminAccess ? <Dashboard /> : <PartnerDashboard />}
					</ProtectedRoute>
				}
			/>

			{/* Full Admin Only Routes */}
			<Route
				path="/add-wallet"
				element={
					<ProtectedRoute requireFullAdmin>
						<AddWallet />
					</ProtectedRoute>
				}
			/>

			<Route
				path="/wallet/:id/edit"
				element={
					<ProtectedRoute>
						<EditWalletWrapper />
					</ProtectedRoute>
				}
			/>

			<Route
				path="/settings"
				element={
					<ProtectedRoute requireFullAdmin>
						<DocumentTemplateProvider>
							<Settings />
						</DocumentTemplateProvider>
					</ProtectedRoute>
				}
			/>

			<Route
				path="/users"
				element={
					<ProtectedRoute requireFullAdmin>
						<UserManagement />
					</ProtectedRoute>
				}
			/>

			{/* KYB — admin + stellar_admin (stellar_admin gets read-only from BFF) */}
			<Route
				path="/kyc"
				element={
					<ProtectedRoute requireAdmin>
						<KYCList />
					</ProtectedRoute>
				}
			/>

			<Route
				path="/settlement-analysis"
				element={
					<ProtectedRoute requireFullAdmin>
						<SettlementAnalysis />
					</ProtectedRoute>
				}
			/>

			<Route
				path="/stellar-admin"
				element={
					<ProtectedRoute requireAdmin>
						<StellarAdmin />
					</ProtectedRoute>
				}
			/>

			{/* Shared Routes (with access control) */}
			<Route
				path="/wallet/:id"
				element={
					<ProtectedRoute>
						<WalletDetailWrapper />
					</ProtectedRoute>
				}
			/>

			{/* Partner View Route - Admin preview of partner perspective */}
			<Route
				path="/wallet/:id/partner"
				element={
					<ProtectedRoute>
						<WalletDetail isPartnerView={true} />
					</ProtectedRoute>
				}
			/>

			<Route
				path="/kyc/:id"
				element={
					<ProtectedRoute>
						<KYCForm />
					</ProtectedRoute>
				}
			/>
		</Routes>
	);
};

/** Public routes rendered outside AuthProvider (no login required). */
const PublicRoutes: React.FC = () => (
	<Routes>
		<Route path="/onboard" element={<Onboard />} />
	</Routes>
);

/** Renders public routes if path matches, otherwise full app. */
const AppRouter: React.FC = () => {
	const location = window.location.pathname;
	if (location.startsWith("/onboard")) {
		return <PublicRoutes />;
	}
	return (
		<AuthProvider>
			<ConfirmDialogProvider>
				<DocumentTemplateProvider>
					<WalletProvider>
						<RouteInvalidationHandler />
						<div className="app">
							<Header />
							<AppRoutes />
							<AIAssistant />
							<Toaster
								position="bottom-right"
								toastOptions={{
									className: "portal-toast",
									style: {
										borderRadius: "var(--border-radius)",
										border: "1px solid var(--color-border)",
										background: "var(--color-white)",
										boxShadow: "var(--shadow-lg)",
									},
									success: {
										iconTheme: {
											primary: "var(--color-success)",
											secondary: "var(--color-white)",
										},
									},
									error: {
										iconTheme: {
											primary: "var(--color-error-500)",
											secondary: "var(--color-white)",
										},
									},
								}}
							/>
						</div>
					</WalletProvider>
				</DocumentTemplateProvider>
			</ConfirmDialogProvider>
		</AuthProvider>
	);
};

const App: React.FC = () => {
	const [trpcClient] = useState(() => createTrpcClient());
	return (
		<trpc.Provider client={trpcClient} queryClient={queryClient}>
			<QueryClientProvider client={queryClient}>
				<ThemeProvider>
					<Router>
						<AppRouter />
					</Router>
				</ThemeProvider>
			</QueryClientProvider>
		</trpc.Provider>
	);
};

export default App;
